import { useCurrentEtudeData } from "components/EtudeForm/hooks/useCurrentEtudeData";
import { useGetAccessoiresInfo } from "components/EtudeForm/hooks/useGetAccessoriesInfo";
import { SavePopup } from "components/popups/SavePopup";
import Button from "components/shared/button";
import ListView from "components/shared/list/listView";
import Loader from "components/shared/loader";
import Scroll from "components/shared/scroll";
import { Text } from "components/shared/text";
import { usePopup } from "providers/PopupProvider";
import React, { JSX, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { APP_URL, EtudeStep, getAppUrl } from "utils/defines";
import { useAuth } from "utils/hooks/useAuth";
import useTranslation from "utils/hooks/useTranslation";
import { InstallationType } from "../../hooks/useGetSolutionsQuery";
import { BasketSummary } from "./BasketSummary";
import { Section } from "./SolutionDetailComponents/Section";
import { SectionsContainer } from "./SolutionDetailComponents/SectionsContainer";
import { FixedNumberQuantityWidget } from "./SolutionDetailComponents/SolutionDetailListItemComponents/QuantityWidget/FixedNumberQuantityWidget";
import { FreeNumberQuantityWidget } from "./SolutionDetailComponents/SolutionDetailListItemComponents/QuantityWidget/FreeNumberQuantityWidget";
import { SolutionDetailListItem } from "./SolutionDetailComponents/SolutionDetailListItemComponents/SolutionDetailListItem";
import { getSolutionID, getSolutionNomDeProposition } from "./helpers";
import { useDispatch } from "react-redux";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import { setEtudeData, setEtudeId } from "store/etudeSlice";
import {
  useUpdateEtudeDataJsonMutation,
  useUpdateProjectTraceMutation,
} from "store/api";
import {
  getLocalStorageTraceId,
  setLocalStorageTraceId,
} from "utils/helpers/localStorage";
import MapValuesToProjectTrace from "utils/helpers/projectTrace";
import { ProjectTrace } from "types/ProjectTrace";
import { ListViewType } from "components/shared/list/useListHook";
import { ConfirmationPopup } from "components/popups/ConfirmationPopup";
import { Popup } from "components/popups/Popup";

enum AccessoireType {
  product,
  accessoires,
  services,
  boiler,
}
export type AccessoireItem = {
  id?: number | string;
  salesreference?: number;
  code: string;
  description?: string;
  name: string | JSX.Element;
  prix: number;
  previewUrl?: string;
  quantite: number;
  tag: string;
  acc_incompatibility?: string;
};

const getIncompatibilityRefs = (accIncompatibility?: string) => {
  if (!accIncompatibility) {
    return [];
  }

  const rawValue = accIncompatibility.trim();

  // Format example: "[6928,6908]"
  const parsed = JSON.parse(rawValue);
  if (Array.isArray(parsed)) {
    return parsed
      .map((value) => String(value).trim())
      .filter(Boolean);
  }
};

export const SolutionAccessoires: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { network } = useSalesNetwork();
  const { etudeId } = useParams();
  const { openPopup, closeLastPopup } = usePopup();
  const { isAuth } = useAuth();
  const { t } = useTranslation();
  const { etudeData, service, json, installationType, solution, isLoading, hasFXBallon } =
    useCurrentEtudeData();
  const [updateEtudeDataJson] = useUpdateEtudeDataJsonMutation();
  const [updateProjectTrace] = useUpdateProjectTraceMutation();
  const { items, selectionInfo } = useGetAccessoiresInfo(
    solution,
    installationType,
    json?.accessoires
  );
  const onTraceProject = useCallback(async () => {
    let id = getLocalStorageTraceId();

    let projectTrace: ProjectTrace = MapValuesToProjectTrace(
      {
        Id: id || 0,
        network: network?.code,
        installationType,
        service,
        systeme: json?.systeme,
        quoteEquipments: items?.panierItems.map((item) => item.code),
        selectedAccs: {
          ...selectionInfo?.selectedAccessoiresNonSupp,
          ...selectionInfo?.selectedAccessoiresSupp,
          ...selectionInfo.selectedBoilers,
          ...selectionInfo.selectedServices,
        },
        ...solution,
        ...json,
      },
      t
    );
    let project = await updateProjectTrace(projectTrace);

    if (project?.data?.id) {
      setLocalStorageTraceId(project.data.id);
    }
  }, [
    installationType,
    network,
    service,
    json,
    items,
    selectionInfo,
    solution,
  ]);

  const saveCallback = useCallback((id: number) => {
    navigate(
      getAppUrl(
        `${APP_URL.Etude}/${APP_URL.SolutionDetailDocuments}`
          .replace(":etudeType", service!)
          .replace(":etudeId", id.toString())
      )
    );
  }, []);

  const {
    selectedAccessoiresNonSupp,
    toggleSelectedAccessoireNonSupp,
    selectedAccessoiresSupp,
    setSelectedAccessoireSuppCount,
    addAccessoireSuppCount,
    decreaseAccessoireSuppCount,
    selectedBoilers,
    toggleSelectedBoilers,
    selectedServices,
    toggleSelectedService,
  } = selectionInfo;

  const openSummaryPopup = useCallback(() => {
    openPopup(
      <SavePopup
        closePopup={() => {
          closeLastPopup();
        }}
        callback={saveCallback}
        application={etudeData?.application || ""}
        subApplication={etudeData?.subApplication}
        systeme={etudeData?.systeme || ""}
        formData={{
          ...json,
          accessoires: {
            selectedAccessoiresNonSupp,
            selectedAccessoiresSupp,
            selectedBoilers,
            selectedServices,
          },
          selectedSolution: solution,
          selectedSolutionID: getSolutionID(solution!),
          step: EtudeStep.Documents,
          stepName: solution
            ? getSolutionNomDeProposition(solution)
            : t("FO_STUDIES_IN_PROGRESS"),
        }}
        saveLocally={!isAuth}
      ></SavePopup>
    );
  }, [
    closeLastPopup,
    etudeData?.application,
    etudeData?.subApplication,
    etudeData?.systeme,
    isAuth,
    json,
    openPopup,
    saveCallback,
    selectedAccessoiresNonSupp,
    selectedAccessoiresSupp,
    selectedBoilers,
    selectedServices,
    solution,
    t,
  ]);

  const handleUpdateJson = async () => {
    const etudeUpdateData = {
      id: etudeId && !isNaN(+etudeId) ? +etudeId : 0,
      name: etudeData?.siteName,
      code: network?.code,
      jsonData: JSON.stringify({
        ...json,
        accessoires: {
          selectedAccessoiresNonSupp,
          selectedAccessoiresSupp,
          selectedBoilers: Object.fromEntries(
            Object.entries(selectedBoilers).filter(([, v]) => v)
          ),
          selectedServices,
        },
        selectedSolution: solution,
        selectedSolutionID: getSolutionID(solution!),
        step: EtudeStep.Documents,
        stepName: solution
          ? getSolutionNomDeProposition(solution)
          : t("FO_STUDIES_IN_PROGRESS"),
      }),
    };

    await updateEtudeDataJson({ ...etudeUpdateData }).then((result: any) => {
      if (result.error) {
        throw new Error(result.error.data.title);
      }

      let newPath = location.pathname.replace(":etudeId", result?.data?.id);
      if (etudeId) {
        newPath = location.pathname.replace(
          new RegExp(etudeId, "g"),
          result?.data?.id
        );
      }

      dispatch(setEtudeId(result?.data?.id));
      dispatch(
        setEtudeData({
          ...result?.data,
          etudeId: result?.data?.id,
        })
      );
      navigate(newPath, {
        replace: true,
      });
    });
  };

  const sections = useMemo(() => {
    if (!items) {
      return [];
    }

    const { panierItems, serviceItems, accessoireItems, boilerItems } = items;
    const result: {
      type: AccessoireType;
      title: string;
      items: AccessoireItem[];
    }[] = [
        {
          type: AccessoireType.product,
          title: "FO_ACC_PRODUCTS_INCL",
          items: panierItems,
        },
        {
          type: AccessoireType.accessoires,
          title: "FO_ACC_NOT_INCL",
          items: accessoireItems,
        },
        {
          type: AccessoireType.services,
          title: "FO_ACC_SERVICES",
          items: serviceItems,
        },
      ];

    if (installationType == InstallationType.hybride) {
      result.splice(1, 0, {
        type: AccessoireType.boiler,
        title: "FO_ACC_BOILER",
        items: boilerItems,
      });
    }

    return result;
  }, [items, installationType]);

  const itemRefs = useMemo(() => {
    const byCode = new Map<string, AccessoireItem>();
    const byId = new Map<string, AccessoireItem>();

    sections.forEach((section) => {
      section.items.forEach((item) => {
        byCode.set(item.code, item);

        if (item.id !== undefined && item.id !== null) {
          byId.set(String(item.id), item);
        }

        if (item.salesreference !== undefined && item.salesreference !== null) {
          byId.set(String(item.salesreference), item);
        }
      });
    });

    return { byCode, byId };
  }, [sections]);

  const selectedAccessoryCodes = useMemo(() => {
    const selected = new Set<string>();

    Object.entries(selectedAccessoiresNonSupp).forEach(([code, isSelected]) => {
      if (isSelected) {
        selected.add(code);
      }
    });

    Object.entries(selectedServices).forEach(([code, isSelected]) => {
      if (isSelected) {
        selected.add(code);
      }
    });

    Object.entries(selectedBoilers).forEach(([code, isSelected]) => {
      if (isSelected) {
        selected.add(code);
      }
    });

    Object.entries(selectedAccessoiresSupp).forEach(([code, count]) => {
      if ((count ?? 0) > 0) {
        selected.add(code);
      }
    });

    return selected;
  }, [
    selectedAccessoiresNonSupp,
    selectedServices,
    selectedBoilers,
    selectedAccessoiresSupp,
  ]);

  const incompatibilityMap = useMemo(() => {
    const map = new Map<string, Set<string>>();

    const addLink = (fromCode: string, toCode: string) => {
      if (!map.has(fromCode)) {
        map.set(fromCode, new Set<string>());
      }
      map.get(fromCode)?.add(toCode);
    };

    sections.forEach((section) => {
      section.items.forEach((item) => {
        const currentCode = item.code;
        const incompatibleRefs = getIncompatibilityRefs(item.acc_incompatibility);

        incompatibleRefs?.forEach((incompatibleRef) => {
          const incompatibleItem =
            itemRefs.byId.get(incompatibleRef) ??
            itemRefs.byCode.get(incompatibleRef);

          const incompatibleCode = incompatibleItem?.code;

          if (!incompatibleCode || incompatibleCode === currentCode) {
            return;
          }

          // Keep incompatibility graph symmetric.
          addLink(currentCode, incompatibleCode);
          addLink(incompatibleCode, currentCode);
        });
      });
    });

    return map;
  }, [sections, itemRefs]);

  const incompatibleDefaultPair = useMemo(() => {
    if (!solution) return null;

    const defaultSelectedCodes = solution.services
      .filter((s: any) => s.dans_panier === true && s.thercodim_active && selectedServices[s.code])
      .map((s: any) => s.code as string);

    for (let i = 0; i < defaultSelectedCodes.length; i++) {
      for (let j = i + 1; j < defaultSelectedCodes.length; j++) {
        const codeA = defaultSelectedCodes[i];
        const codeB = defaultSelectedCodes[j];
        if (incompatibilityMap.get(codeA)?.has(codeB)) {
          return {
            nameA: (itemRefs.byCode.get(codeA)?.name as string) ?? codeA,
            nameB: (itemRefs.byCode.get(codeB)?.name as string) ?? codeB,
          };
        }
      }
    }
    return null;
  }, [solution, selectedServices, incompatibilityMap, itemRefs]);

  const openIncompatibilityPopup = useCallback(
    (blockedCode: string, selectedCode?: string | null) => {
      openPopup(
        <Popup title={t("FO_POPUP_INCOMPATIBILITE")} fixSize="small">
          {() => (
            <group data-space="20" data-direction="column">
              <text data-wrap="wrap" data-text-size="medium">
                {t("FO_POPUP_MESSAGE_INCOMPATIBILITE", [blockedCode, selectedCode ?? ""], "text")}
              </text>

            </group>
          )}
        </Popup>
      );
    },
    [openPopup, t]
  );

  const proceedToSave = useCallback(async () => {

    if (hasFXBallon(solution)) {
      openPopup(
        <ConfirmationPopup
          title={t("FO_POPUP_INFORMATION")}
          message={t("FO_POPUP_ETUDE_EXESPE")}
          isHtmlMessage
          positiveButtonText={t("FO_OK")}
          hideNegativeButton
          positiveCallback={() => {
            closeLastPopup?.();
          }}
        />
      );
    } else {
      isAuth && (await handleUpdateJson());
      await onTraceProject();

      openSummaryPopup();
    }
    return;
  }, [handleUpdateJson, hasFXBallon, isAuth, onTraceProject, openPopup, openSummaryPopup, solution, t]);

  return (
    <view data-gap="30" data-scroll="">
      {isLoading && <Loader />}
      {solution && (
        <>
          <SectionsContainer>
            {sections.map(
              (section, i) =>
                section?.items?.length > 0 && (
                  <Section
                    title={section.title}
                    index={i}
                    key={`${section.title}_${i}`}
                  >
                    {(section.type === AccessoireType.accessoires) && (
                      <group>{t("FO_ACC_NOT_INCL_COMMENT")}</group>
                    )}

                    {(section.type === AccessoireType.boiler && solution.communicant &&
                      <>
                        <div
                          data-space-top="10"
                          dangerouslySetInnerHTML={{
                            __html: t("FO_INFO_BOILER_INSTALLATION_ATLANTIC_ONLY"),
                          }}
                        />
                        <group>{t("FO_MANDATORY_SERVICES_OFFER")}</group>
                      </>
                    )}
                    {(section.type === AccessoireType.services) && (
                      <group>{t("FO_ACC_SERVICES_COMMENT")}</group>
                    )}
                    <ListView
                      view={ListViewType.Block}
                      keyField="code"
                      data-template="450"
                      dataSource={section.items.map((item) => ({
                        ...item,
                        type: section.type,
                      }))}
                      scrollerProps={{}}
                      listProps={{
                        "data-gap": "10",
                        "data-space": "10",
                      }}
                      itemProps={{
                        className: "custom",
                        "data-direction": "column",
                      }}
                      itemComponent={({ data }) => {
                        const { code, quantite, name } = data;

                        const min = 0;
                        const fixedQuantityAccessoires =
                          section.type == AccessoireType.accessoires &&
                          data.tag !== "SUPP";
                        //                    const max = quantite;
                        const currentValue =
                          section.type == AccessoireType.accessoires &&
                            !fixedQuantityAccessoires
                            ? selectedAccessoiresSupp[code]
                            : 0;
                        const isSelected =
                          (fixedQuantityAccessoires &&
                            selectedAccessoiresNonSupp[code]) ||
                          (section.type == AccessoireType.services &&
                            selectedServices[code]) ||
                          (section.type == AccessoireType.boiler &&
                            !!selectedBoilers[code]);

                        let incompatibleWithCode: string | null = null;
                        let incompatibleWithCodeName: string | null = null;

                        const isAlreadySelected =
                          isSelected || (!!currentValue && currentValue > 0);

                        if (!isAlreadySelected) {
                          for (const selectedCode of selectedAccessoryCodes) {
                            if (incompatibilityMap.get(selectedCode)?.has(code)) {
                              incompatibleWithCode = selectedCode;
                              incompatibleWithCodeName = itemRefs.byCode.get(selectedCode)?.name as string;
                              break;
                            }
                          }
                        }

                        const isIncompatible = !!incompatibleWithCode;

                        const handleBlockedAction = () => {
                          if (incompatibleWithCode) {
                            openIncompatibilityPopup(name, incompatibleWithCodeName);
                          }
                        };

                        const isSelectedPac = data.tag === "HP";

                        return (
                          <>
                            <group
                              data-width="auto"
                              data-height="24"
                              data-border="no"
                              style={{ boxShadow: "none" }}
                            >
                              {isSelectedPac ? (
                                <Text
                                  data-color="white"
                                  data-uppercase=""
                                  data-background="main"
                                  data-height="fit"
                                  data-space-vertical="8"
                                  data-space-horizontal="16"
                                  data-text-size="12"
                                  style={{
                                    lineHeight: "100%",
                                    boxShadow: "0 0 0 1px var(--main-color)",
                                  }}
                                >
                                  FO_ACC_PAC
                                </Text>
                              ) : (
                                <></>
                              )}
                            </group>
                            <SolutionDetailListItem
                              isSelected={isSelected || !!currentValue}
                              style={{
                                height: "100%",
                                ...(isIncompatible && {
                                  opacity: 0.55,
                                  filter: "grayscale(0.5)",
                                }),
                                ...(isSelectedPac && {
                                  borderTopLeftRadius: 0,
                                  boxShadow: "0 0 0 1px var(--main-color)",
                                }),
                              }}
                              {...data}
                            >
                              {section.type === AccessoireType.accessoires &&
                                !fixedQuantityAccessoires && (
                                  <FreeNumberQuantityWidget
                                    min={min}
                                    value={currentValue}
                                    onChange={(value) =>
                                      isIncompatible
                                        ? handleBlockedAction()
                                        : setSelectedAccessoireSuppCount(
                                          code,
                                          value
                                        )
                                    }
                                    onIncrease={() =>
                                      isIncompatible
                                        ? handleBlockedAction()
                                        : addAccessoireSuppCount(code)
                                    }
                                    onDecrease={() =>
                                      isIncompatible
                                        ? handleBlockedAction()
                                        : decreaseAccessoireSuppCount(code)
                                    }
                                  />
                                )}
                              {fixedQuantityAccessoires && (
                                <FixedNumberQuantityWidget
                                  isSelected={isSelected}
                                  quantity={quantite}
                                  onToggle={() =>
                                    isIncompatible
                                      ? handleBlockedAction()
                                      : toggleSelectedAccessoireNonSupp(code)
                                  }
                                />
                              )}
                              {section.type === AccessoireType.services && (
                                <FixedNumberQuantityWidget
                                  isSelected={isSelected}
                                  quantity={quantite}
                                  onToggle={() =>
                                    isIncompatible
                                      ? handleBlockedAction()
                                      : toggleSelectedService(code)
                                  }
                                />
                              )}
                              {section.type === AccessoireType.boiler && (
                                <FixedNumberQuantityWidget
                                  isSelected={isSelected}
                                  quantity={quantite}
                                  disabled={!!solution.communicant}
                                  onToggle={() =>
                                    isIncompatible
                                      ? handleBlockedAction()
                                      : toggleSelectedBoilers(code)
                                  }
                                />
                              )}
                            </SolutionDetailListItem>
                          </>
                        );
                      }}
                    />
                    {solution.communicant && section.type === AccessoireType.product &&
                      installationType == InstallationType.hybride && (
                        <div
                          data-space-top="10"
                          dangerouslySetInnerHTML={{
                            __html: t("FO_INFO_NAVISTEM_ATLANTIC_MANDATORY"),
                          }}
                        />
                      )}
                  </Section>
                )
            )}
          </SectionsContainer>
          <group
            data-sticky="bottom"
            data-border="overprint"
            data-background="white"
          >
            <Scroll itemContainerSelector="#footerItems">
              <group data-wrap="no" data-align="center">
                <BasketSummary
                  {...items!}
                  selectedAccessoiresNonSupp={selectedAccessoiresNonSupp}
                  selectedAccessoiresSupp={selectedAccessoiresSupp}
                  selectedBoilers={selectedBoilers}
                  selectedServices={selectedServices}
                  solution={solution}
                />

                <group
                  data-gap="15"
                  data-space-horizontal="30"
                  data-space-vertical="15"
                  data-width="auto"
                  data-wrap="no"
                >
                  <Button
                    data-background={hasFXBallon(solution) ? "grey-color-mid" : "secondary"}
                    data-color="white"
                    large
                    data-width="autofit"
                    text="FO_BUTTON_OBTAIN_SYNTHESIS"
                    icon="check"
                    style={hasFXBallon(solution) ? { opacity: 0.5, filter: "grayscale(0.5)" } : undefined}
                    onClick={async () => {
                      if (incompatibleDefaultPair) {
                        openIncompatibilityPopup(incompatibleDefaultPair.nameA, incompatibleDefaultPair.nameB)
                        return;
                      }

                      await proceedToSave();
                    }}
                  />
                </group>
              </group>
            </Scroll>
          </group>
        </>
      )}
    </view>
  );
};
